/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  Button,
  ICON_LIBRARY_BOOKS,
  LINK_TYPE_STANDALONE,
  Link,
  SPINNER_TYPE_REGULAR,
  Spinner,
  TextField,
  TextFieldProps,
  WIZARD_DIRECTION_NEXT,
} from '@squiz/sds';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import {
  ComponentServiceGetComponentDto,
  EntityComponents,
  EntityComponentsItem,
  getComponentService,
} from '../../../../Services/component';
import { ADD_COMPONENT_TO_SET_DATA_IDS } from '../../AddComponentToSet.constants';
import { AvailableComponentsFormProps } from './AvailableComponentsForm.props';
import { SelectedComponentProps } from './SelectedComponentProps.props';
import { AvailableComponentsFormComponent } from './elements/AvailableComponentsFormComponent';

/**
 * The form for choosing a component to add to set.
 */
export const AvailableComponentsForm = (props: AvailableComponentsFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { componentSetWebpath, componentSetDisplayName, close, setButtonKeys, setButtons, updateStep } = props;

  const [page, setPage] = useState<number>(1);
  const [nextPage, setNextPage] = useState<boolean>(true);
  const [componentData, setComponentData] = useState<Array<EntityComponentsItem>>([]);
  const [loadingResult, setLoadingResult] = useState<boolean>(true);
  const [componentsCount, setComponentsCount] = useState<number>(0);
  const [inSetComponents, setInSetComponents] = useState<Array<EntityComponentsItem>>([]);
  const [searchComponentsString, setSearchComponentsString] = useState<string | undefined>(undefined);

  // Labels
  const labels = useMemo(() => {
    return {
      addToSet: t(COMPONENT_SERVICE_STRINGS.addToSet),
      backToAllComponents: t(COMPONENT_SERVICE_STRINGS.backToAllComponents),
      cancel: t(COMPONENT_SERVICE_STRINGS.cancel),
      listTitle: t(COMPONENT_SERVICE_STRINGS.listTitle),
      loadMore: t(COMPONENT_SERVICE_STRINGS.loadMore),
      searchComponent: t(COMPONENT_SERVICE_STRINGS.searchComponent),
      selectComponent: t(COMPONENT_SERVICE_STRINGS.selectComponent),
    };
  }, [t]);

  // Component service
  const componentService = getComponentService();

  const componentListFooter: JSX.Element = (
    <div className="add-to-set__list__show-more">
      <Button
        data-id={ADD_COMPONENT_TO_SET_DATA_IDS.loadMore}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => setPage(page + 1)}
        text={labels.loadMore}
        theme={BUTTON_THEME_DEFAULT}
      />
    </div>
  );

  const listSummaryDefault: JSX.Element = (
    <span className="add-to-set__list__summary-result">
      Showing <strong>{componentData.length}</strong> of total <strong>{componentsCount}</strong> available components,
      with <strong>{inSetComponents.length}</strong> already in this set.
    </span>
  );
  const listSummarySearch: JSX.Element = (
    <>
      <Link
        onClick={(e): void => {
          e.preventDefault();
          setLoadingResult(true);
          setPage(1);
          setNextPage(true);
          setSearchComponentsString(undefined);
        }}
        text={labels.backToAllComponents}
        type={LINK_TYPE_STANDALONE}
      />
      <span className=".add-to-set__list__summary-separator"> | </span>
      {listSummaryDefault}
    </>
  );

  const listTitle: JSX.Element = (
    <div className="add-to-set__list__header-title">
      <div>{labels.listTitle}</div>
      <div>
        <strong>{componentSetDisplayName}</strong>
      </div>
    </div>
  );

  const searchComponentsInputRef = useRef<HTMLInputElement>(null);
  const searchFieldProperties: TextFieldProps = {
    disabled: loadingResult,
    fieldLabel: labels.searchComponent,
    hideLabel: true,
    id: ADD_COMPONENT_TO_SET_DATA_IDS.searchComponentsInput,
    key: searchComponentsString,
    name: ADD_COMPONENT_TO_SET_DATA_IDS.searchComponentsInput,
    onKeyDown: (e) => {
      if (e.key === 'Enter') {
        const search = searchComponentsInputRef.current?.value.trim();
        setLoadingResult(true);
        setPage(1);
        setNextPage(true);
        setSearchComponentsString(search);
      }
    },
    value: searchComponentsString,
  };

  const fetchComponents = useCallback(
    (controller?: AbortController): void => {
      const dto: ComponentServiceGetComponentDto = { page };
      if (searchComponentsString !== undefined) {
        dto.search = searchComponentsString;
      }

      // Send request to get latest component sets with current page
      lastValueFrom(componentService.getComponent(dto))
        .then((res) => {
          // Don't update state if the request was aborted
          if (controller?.signal.aborted) {
            return;
          }

          // At this point, we know a tenant exists so we will get a non-null value
          const response = res as EntityComponents;

          // Check if there is another page after this one
          if (!response.pageInfo.next) {
            setNextPage(false);
          }

          setComponentsCount(response.pageInfo.totalItems);
          setInSetComponents((previous) => [
            ...(page > 1 ? previous : []),
            ...response.items.filter((item) => item.componentSets.find((set) => set.webPath === componentSetWebpath)),
          ]);

          // Update the component sets with updated data
          setComponentData((previous) => [...(page > 1 ? previous : []), ...response.items]);

          // Update the loading flag last so the loading state doesn't prematurely disappear
          setLoadingResult(false);
          return;
        })
        .catch(() => {
          // Do nothing for now
        });
    },
    [componentService, componentSetWebpath, page, searchComponentsString],
  );

  useEffect(() => {
    // Set up controller object to abort any pending requests which may be still running.
    const controller = new AbortController();

    fetchComponents(controller);

    // Stop the previous request on unmount
    return (): void => controller.abort();
  }, [fetchComponents, page, searchComponentsString]);

  // Set buttons
  useEffect(() => {
    const cancelButton = (
      <Button
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const addToSetButton = (
      <Button
        data-id={ADD_COMPONENT_TO_SET_DATA_IDS.addToSetButton}
        disabled
        format={BUTTON_FORMAT_STRONG}
        text={labels.addToSet}
      />
    );

    setButtonKeys([`cancel`, `add`]);
    setButtons([cancelButton, addToSetButton]);
  }, [close, labels, setButtonKeys, setButtons, updateStep]);

  // Map  over the component set array and look for components
  const componentItems: Array<JSX.Element> = componentData.map((component: EntityComponentsItem, index: number) => {
    const componentID: string = component.name;
    const isInComponentSet = !!inSetComponents.find((component) => component.name === componentID);
    const handleComponentSelect = (component: SelectedComponentProps): void => {
      if (!isInComponentSet) {
        updateStep(WIZARD_DIRECTION_NEXT, component);
      }
    };

    return (
      <AvailableComponentsFormComponent
        component={component}
        componentID={componentID}
        handleSelect={handleComponentSelect}
        isInComponentSet={isInComponentSet}
        key={index}
      />
    );
  });

  return (
    <div className="add-to-set__step-1" data-id={ADD_COMPONENT_TO_SET_DATA_IDS.addToSetStep1}>
      <div className="add-to-set__list__header">
        {listTitle}
        <div className="add-to-set__list__header-search">
          <TextField {...searchFieldProperties} icon={ICON_LIBRARY_BOOKS} ref={searchComponentsInputRef}></TextField>
        </div>
      </div>
      <hr className="add-to-set__list__header-divider"></hr>
      {loadingResult ? (
        <div className="add-to-set__loading">
          <Spinner type={SPINNER_TYPE_REGULAR} />
        </div>
      ) : (
        <>
          <div className="add-to-set__list__summary">
            {searchComponentsString === undefined ? listSummaryDefault : listSummarySearch}
          </div>
          <div className="add-to-set__list">{componentItems}</div>
          {/* Show loading component if next page is being requested */}
          {nextPage && componentListFooter}
        </>
      )}
    </div>
  );
};
